import React from 'react'
import {
  HStack,
  Button,
  IconButton,
  Tooltip,
  Divider,
  useColorModeValue,
  Box,
  Text,
} from '@chakra-ui/react'
import {
  MdInsertDriveFile,
  MdFolderOpen,
  MdSave,
  MdDownload,
  MdPictureAsPdf,
  MdCheckCircle,
  MdWarning,
  MdNearMe,
  MdTouchApp,
  MdGridOn,
  MdTableRows,
  MdAddCircleOutline,
  MdOutlineCropLandscape,
  MdRadioButtonUnchecked,
  MdPolyline,
  MdTextFields,
  MdUndo,
  MdRedo,
  MdContentCut,
  MdContentCopy,
  MdContentPaste,
  MdDelete,
  MdZoomIn,
  MdZoomOut,
  MdGridGoldenratio,
  MdAlignVerticalTop,
  MdAlignVerticalCenter,
  MdAlignVerticalBottom,
  MdAlignHorizontalLeft,
  MdAlignHorizontalCenter,
  MdAlignHorizontalRight,
  MdVerticalDistribute,
  MdHorizontalDistribute,
  MdCenterFocusStrong,
  MdFormatListNumbered,
} from 'react-icons/md'
import { FaCircleNotch } from 'react-icons/fa'


export function Toolbar({
  activeTool,
  setActiveTool,
  onOpenAutoLabel,
  onNewPlan,
  onOpenLoad,
  onSave,
  isSaving,
  onExportJson,
  onExportPdf,
  onOpenValidation,
  validationErrorCount = 0,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  canCopy,
  canPaste,
  onCut,
  onCopy,
  onPaste,
  onDeleteSelected,
  hasSelection,
  canAlign,
  onAlign,
  zoom = 1,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onCenterView,
  snapToGrid = true,
  onToggleSnapToGrid,
}) {
  const bg = useColorModeValue('#FFFFFF', '#1E293B')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const iconColor = useColorModeValue('gray.700', 'gray.200')
  const isValid = validationErrorCount === 0

  return (
    <Box
      w="100%"
      h="48px"
      bg={bg}
      borderBottom="1px solid"
      borderColor={borderColor}
      px={3}
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      boxShadow="0px 2px 4px rgba(0, 0, 0, 0.04)"
      zIndex={15}
    >
      <HStack
        spacing={1}
        overflowX="auto"
        overflowY="hidden"
        py={1}
        w="100%"
        align="center"
        sx={{
          '& > *': { flexShrink: 0 },
          '&::-webkit-scrollbar': { height: '4px' },
          '&::-webkit-scrollbar-track': { bg: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bg: 'whiteAlpha.300', borderRadius: '4px' },
        }}
      >
        {/* =========================================
            SECTION 1: FILE & VALIDATION
           ========================================= */}
        <Tooltip label="Create a New Plan" placement="bottom">
          <IconButton
            icon={<MdInsertDriveFile />}
            aria-label="Create a New Plan"
            size="sm"
            variant="ghost"
            color={iconColor}
            onClick={onNewPlan}
          />
        </Tooltip>

        <Tooltip label="Load Plan" placement="bottom">
          <IconButton
            icon={<MdFolderOpen />}
            aria-label="Load Plan"
            size="sm"
            variant="ghost"
            color={iconColor}
            onClick={onOpenLoad}
          />
        </Tooltip>

        <Tooltip label="Save Plan [Ctrl + S]" placement="bottom">
          <IconButton
            icon={<MdSave />}
            aria-label="Save Plan"
            size="sm"
            variant="ghost"
            color="brand.500"
            isLoading={isSaving}
            onClick={onSave}
          />
        </Tooltip>

        <Tooltip label="Download as JSON" placement="bottom">
          <IconButton
            icon={<MdDownload />}
            aria-label="Download JSON"
            size="sm"
            variant="ghost"
            color="teal.400"
            onClick={onExportJson}
          />
        </Tooltip>

        <Tooltip label="Export to PDF" placement="bottom">
          <IconButton
            icon={<MdPictureAsPdf />}
            aria-label="Export to PDF"
            size="sm"
            variant="ghost"
            color={iconColor}
            onClick={onExportPdf}
          />
        </Tooltip>

        <Tooltip
          label={isValid ? 'Validation: All unique & ready' : `Validation: ${validationErrorCount} conflict(s) found`}
          placement="bottom"
        >
          <Button
            size="xs"
            h="28px"
            px={2.5}
            leftIcon={isValid ? <MdCheckCircle size={14} /> : <MdWarning size={14} />}
            colorScheme={isValid ? 'green' : 'red'}
            variant="solid"
            fontSize="xs"
            fontWeight="700"
            onClick={onOpenValidation}
            borderRadius="4px"
          >
            {isValid ? 'VALID' : `${validationErrorCount} ERRORS`}
          </Button>
        </Tooltip>

        <Divider orientation="vertical" h="24px" mx={1} />

        {/* =========================================
            SECTION 2: SELECTION MODES
           ========================================= */}
        <Tooltip label="Select rows or shapes [V]" placement="bottom">
          <IconButton
            icon={<MdNearMe />}
            aria-label="Select rows or shapes [V]"
            size="sm"
            variant={activeTool === 'select' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'select' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('select')}
          />
        </Tooltip>

        <Tooltip label="Select seats [S]" placement="bottom">
          <IconButton
            icon={<MdTouchApp />}
            aria-label="Select seats [S]"
            size="sm"
            variant={activeTool === 'seat-select' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'seat-select' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('seat-select')}
          />
        </Tooltip>

        <Tooltip label="Auto Label Rows & Number Seats (Top to Bottom visual ordering)" placement="bottom">
          <Button
            size="xs"
            h="28px"
            px={2.5}
            leftIcon={<MdFormatListNumbered size={14} />}
            variant="outline"
            colorScheme="brand"
            fontSize="xs"
            fontWeight="600"
            borderRadius="4px"
            onClick={onOpenAutoLabel}
          >
            Auto Label
          </Button>
        </Tooltip>

        <Divider orientation="vertical" h="24px" mx={1} />

        {/* =========================================
            SECTION 3: INTERACTIVE CREATION TOOLS
           ========================================= */}
        <Tooltip label="Add rows [B] (Drag 2D seating block)" placement="bottom">
          <IconButton
            icon={<MdGridOn />}
            aria-label="Add rows [B]"
            size="sm"
            variant={activeTool === 'add-rows' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'add-rows' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('add-rows')}
          />
        </Tooltip>

        <Tooltip label="Add row [N] (Drag angled line)" placement="bottom">
          <IconButton
            icon={<MdTableRows />}
            aria-label="Add row [N]"
            size="sm"
            variant={activeTool === 'add-row' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'add-row' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('add-row')}
          />
        </Tooltip>

        <Tooltip label="Add seat [M] (Click to drop single seat)" placement="bottom">
          <IconButton
            icon={<MdAddCircleOutline />}
            aria-label="Add seat [M]"
            size="sm"
            variant={activeTool === 'add-seat' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'add-seat' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('add-seat')}
          />
        </Tooltip>

        <Tooltip label="Add rectangle [R] (Drag to size)" placement="bottom">
          <IconButton
            icon={<MdOutlineCropLandscape />}
            aria-label="Add rectangle [R]"
            size="sm"
            variant={activeTool === 'add-rect' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'add-rect' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('add-rect')}
          />
        </Tooltip>

        <Tooltip label="Add circle [C] (Regular circle)" placement="bottom">
          <IconButton
            icon={<MdRadioButtonUnchecked />}
            aria-label="Add circle [C]"
            size="sm"
            variant={activeTool === 'add-circle' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'add-circle' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('add-circle')}
          />
        </Tooltip>

        <Tooltip label="Add ellipse [E] (Independent W & H)" placement="bottom">
          <IconButton
            icon={<FaCircleNotch style={{ transform: 'scaleX(1.4)' }} />}
            aria-label="Add ellipse [E]"
            size="sm"
            variant={activeTool === 'add-ellipse' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'add-ellipse' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('add-ellipse')}
          />
        </Tooltip>

        <Tooltip label="Add polygon [P] (Click vertices, Enter to close, Esc to cancel)" placement="bottom">
          <IconButton
            icon={<MdPolyline />}
            aria-label="Add polygon [P]"
            size="sm"
            variant={activeTool === 'add-polygon' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'add-polygon' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('add-polygon')}
          />
        </Tooltip>

        <Tooltip label="Add text [T] (Click to place text)" placement="bottom">
          <IconButton
            icon={<MdTextFields />}
            aria-label="Add text [T]"
            size="sm"
            variant={activeTool === 'add-text' ? 'solid' : 'ghost'}
            colorScheme={activeTool === 'add-text' ? 'brand' : 'gray'}
            onClick={() => setActiveTool('add-text')}
          />
        </Tooltip>

        <Divider orientation="vertical" h="24px" mx={1} />

        {/* =========================================
            SECTION 4: HISTORY
           ========================================= */}
        <Tooltip label="Undo [Ctrl+Z]" placement="bottom">
          <IconButton
            icon={<MdUndo />}
            aria-label="Undo [Ctrl+Z]"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canUndo}
            onClick={onUndo}
          />
        </Tooltip>

        <Tooltip label="Redo [Ctrl+Y]" placement="bottom">
          <IconButton
            icon={<MdRedo />}
            aria-label="Redo [Ctrl+Y]"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canRedo}
            onClick={onRedo}
          />
        </Tooltip>

        <Divider orientation="vertical" h="24px" mx={1} />

        {/* =========================================
            SECTION 5: CLIPBOARD & DELETE
           ========================================= */}
        <Tooltip label="Cut [Ctrl+X]" placement="bottom">
          <IconButton
            icon={<MdContentCut />}
            aria-label="Cut [Ctrl+X]"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canCopy}
            onClick={onCut}
          />
        </Tooltip>

        <Tooltip label="Copy [Ctrl+C]" placement="bottom">
          <IconButton
            icon={<MdContentCopy />}
            aria-label="Copy [Ctrl+C]"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canCopy}
            onClick={onCopy}
          />
        </Tooltip>

        <Tooltip label="Paste [Ctrl+V]" placement="bottom">
          <IconButton
            icon={<MdContentPaste />}
            aria-label="Paste [Ctrl+V]"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canPaste}
            onClick={onPaste}
          />
        </Tooltip>

        <Tooltip label="Delete [Del]" placement="bottom">
          <IconButton
            icon={<MdDelete />}
            aria-label="Delete [Del]"
            size="sm"
            variant="ghost"
            color={hasSelection ? 'red.500' : iconColor}
            isDisabled={!hasSelection}
            onClick={onDeleteSelected}
          />
        </Tooltip>

        <Divider orientation="vertical" h="24px" mx={1} />

        {/* =========================================
            SECTION 5.5: ALIGNMENT
           ========================================= */}
        <Tooltip label="Align Top" placement="bottom">
          <IconButton
            icon={<MdAlignVerticalTop />}
            aria-label="Align Top"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canAlign}
            onClick={() => onAlign('top')}
          />
        </Tooltip>
        <Tooltip label="Align Vertical Center" placement="bottom">
          <IconButton
            icon={<MdAlignVerticalCenter />}
            aria-label="Align Vertical Center"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canAlign}
            onClick={() => onAlign('middle')}
          />
        </Tooltip>
        <Tooltip label="Align Bottom" placement="bottom">
          <IconButton
            icon={<MdAlignVerticalBottom />}
            aria-label="Align Bottom"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canAlign}
            onClick={() => onAlign('bottom')}
          />
        </Tooltip>
        <Tooltip label="Align Left" placement="bottom">
          <IconButton
            icon={<MdAlignHorizontalLeft />}
            aria-label="Align Left"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canAlign}
            onClick={() => onAlign('left')}
          />
        </Tooltip>
        <Tooltip label="Align Horizontal Center" placement="bottom">
          <IconButton
            icon={<MdAlignHorizontalCenter />}
            aria-label="Align Horizontal Center"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canAlign}
            onClick={() => onAlign('center')}
          />
        </Tooltip>
        <Tooltip label="Align Right" placement="bottom">
          <IconButton
            icon={<MdAlignHorizontalRight />}
            aria-label="Align Right"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canAlign}
            onClick={() => onAlign('right')}
          />
        </Tooltip>
        <Tooltip label="Distribute Horizontally" placement="bottom">
          <IconButton
            icon={<MdHorizontalDistribute />}
            aria-label="Distribute Horizontally"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canAlign}
            onClick={() => onAlign('distribute-h')}
          />
        </Tooltip>
        <Tooltip label="Distribute Vertically" placement="bottom">
          <IconButton
            icon={<MdVerticalDistribute />}
            aria-label="Distribute Vertically"
            size="sm"
            variant="ghost"
            color={iconColor}
            isDisabled={!canAlign}
            onClick={() => onAlign('distribute-v')}
          />
        </Tooltip>

        <Divider orientation="vertical" h="24px" mx={1} />

        {/* =========================================
            SECTION 6: ZOOM & VIEWPORT
           ========================================= */}
        <Tooltip label="Zoom Out [Ctrl + -]" placement="bottom">
          <IconButton
            icon={<MdZoomOut />}
            aria-label="Zoom Out"
            size="sm"
            variant="ghost"
            color={iconColor}
            onClick={onZoomOut}
          />
        </Tooltip>

        <Tooltip label="Fit All to Screen & Center View [Ctrl + 0]" placement="bottom">
          <Box
            as="button"
            px={2}
            py={1}
            borderRadius="4px"
            _hover={{ bg: 'whiteAlpha.200' }}
            onClick={onCenterView || onResetZoom}
          >
            <Text fontSize="xs" fontWeight="700" color={iconColor} minW="40px" textAlign="center">
              {Math.round(zoom * 100)}%
            </Text>
          </Box>
        </Tooltip>

        <Tooltip label="Zoom In [Ctrl + +]" placement="bottom">
          <IconButton
            icon={<MdZoomIn />}
            aria-label="Zoom In"
            size="sm"
            variant="ghost"
            color={iconColor}
            onClick={onZoomIn}
          />
        </Tooltip>

        <Tooltip label="Center Canvas & Fit [Ctrl + 0]" placement="bottom">
          <IconButton
            icon={<MdCenterFocusStrong />}
            aria-label="Center Canvas"
            size="sm"
            variant="ghost"
            color={iconColor}
            onClick={onCenterView || onResetZoom}
          />
        </Tooltip>

        <Divider orientation="vertical" h="24px" mx={1} />

        {/* =========================================
            SECTION 7: GRID
           ========================================= */}
        <Tooltip label={snapToGrid ? 'Grid Snap: ON' : 'Grid Snap: OFF'} placement="bottom">
          <IconButton
            icon={<MdGridGoldenratio />}
            aria-label="Toggle Grid"
            size="sm"
            variant={snapToGrid ? 'solid' : 'ghost'}
            colorScheme={snapToGrid ? 'brand' : 'gray'}
            onClick={onToggleSnapToGrid}
          />
        </Tooltip>
      </HStack>
    </Box>
  )
}
